import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import { postChat } from '../_shared/chatService';
import { buildChatRequest, ScreenContextSlice, ChatIntent } from './buildChatRequest';
import {
  INITIAL_DRAFTS,
  INITIAL_SECTION_STATUSES,
  SECTIONS,
  SectionStatus,
  isEditCommand,
  isQuestion,
  parseChatResponse,
} from './prototype3Data';

const CHAT_SCOPE = 'mock-outline';

type ScreenId = '01-welcome' | '02-draft-builder';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SendChatOptions {
  intentOverride?: ChatIntent;
  sliceOverride?: Partial<ScreenContextSlice>;
}

interface Prototype3ContextValue {
  currentScreen: ScreenId;
  goToScreen: (id: ScreenId) => void;
  chatHistory: ChatMessage[];
  isChatPending: boolean;
  sendChat: (message: string, slice: ScreenContextSlice) => Promise<void>;
  sendUserMessage: (message: string, options?: SendChatOptions) => Promise<void>;
  drafts: Record<string, string>;
  sectionStatuses: Record<string, SectionStatus>;
  currentSectionId: string;
  setCurrentSectionId: (id: string) => void;
  sectionDropdownOpen: boolean;
  setSectionDropdownOpen: (open: boolean) => void;
  toggleSectionDropdown: () => void;
}

const Prototype3Context = createContext<Prototype3ContextValue | null>(null);

const DEFAULT_SECTION_ID = 'postsecondary-readiness';

interface Prototype3ProviderProps {
  children: ReactNode;
}

const Prototype3Provider = ({ children }: Prototype3ProviderProps) => {
  const [currentScreen, setCurrentScreen] = useState<ScreenId>('01-welcome');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isChatPending, setIsChatPending] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>(INITIAL_DRAFTS);
  const [sectionStatuses, setSectionStatuses] = useState<Record<string, SectionStatus>>(INITIAL_SECTION_STATUSES);
  const [currentSectionId, setCurrentSectionId] = useState<string>(DEFAULT_SECTION_ID);
  const [sectionDropdownOpen, setSectionDropdownOpen] = useState<boolean>(false);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const goToScreen = useCallback((id: ScreenId) => {
    setCurrentScreen(id);
  }, []);

  const toggleSectionDropdown = useCallback(() => {
    setSectionDropdownOpen((prev) => !prev);
  }, []);

  const sendChat = useCallback(
    async (message: string, slice: ScreenContextSlice) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      setChatHistory((prev) => [...prev, { role: 'user', content: trimmed }]);
      setIsChatPending(true);

      const targetSectionId = slice.active_section_id;

      try {
        const eoaId = userDetails?.eoaId;
        const accessToken = userDetails?.access_token ?? '';
        if (!eoaId || !accessToken) {
          throw new Error('Missing EOA or access token');
        }

        const payload = buildChatRequest({
          message: trimmed,
          screenId: currentScreen,
          slice,
          conversationId,
          scope: CHAT_SCOPE,
          sectionStatuses,
          eoaId,
        });
        const { conversation_id, response } = await postChat(payload, accessToken);
        setConversationId(conversation_id);

        const parsed = parseChatResponse(response);
        const isKnownSection = (id?: string): id is string => Boolean(id) && SECTIONS.some((s) => s.id === id);

        const approveTarget = parsed.approve
          ? isKnownSection(parsed.approve.sectionId)
            ? parsed.approve.sectionId
            : targetSectionId
          : undefined;
        if (approveTarget) {
          setSectionStatuses((prev) => ({ ...prev, [approveTarget]: 'complete' }));
        }

        const draftTarget =
          parsed.draft && (isKnownSection(parsed.draft.sectionId) ? parsed.draft.sectionId : targetSectionId);
        const draftBody = draftTarget ? parsed.draft?.body : undefined;
        if (draftTarget && draftBody) {
          setDrafts((prev) => ({ ...prev, [draftTarget]: draftBody }));
          setSectionStatuses((prev) =>
            prev[draftTarget] === 'complete' ? prev : { ...prev, [draftTarget]: 'in-progress' }
          );
        }

        const switchTarget =
          parsed.switchTo && isKnownSection(parsed.switchTo.sectionId) ? parsed.switchTo.sectionId : undefined;
        if (switchTarget) {
          setCurrentSectionId(switchTarget);
          setSectionDropdownOpen(false);
        }

        const chatText = parsed.chat ? parsed.chat : draftBody ? '✓ Draft updated. See the report on the right.' : '';
        if (chatText) {
          setChatHistory((prev) => [...prev, { role: 'assistant', content: chatText }]);
        }
      } catch (error) {
        handleOpenErrorModal('Prototype chat request failed', error as Error);
        setChatHistory((prev) => [
          ...prev,
          { role: 'assistant', content: '(chat backend unavailable — this is a prototype)' },
        ]);
      } finally {
        setIsChatPending(false);
      }
    },
    [chatHistory, conversationId, currentScreen, sectionStatuses, userDetails, handleOpenErrorModal]
  );

  const sendUserMessage = useCallback(
    async (message: string, options?: SendChatOptions) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      const targetSectionId = options?.sliceOverride?.active_section_id ?? currentSectionId;
      const currentDraft = drafts[targetSectionId] ?? '';
      const hasDraft = currentDraft.trim().length > 0;
      const looksLikeEdit = isEditCommand(trimmed);
      const looksLikeQuestion = isQuestion(trimmed);
      const inferredIntent: ChatIntent = options?.intentOverride
        ? options.intentOverride
        : looksLikeEdit
          ? hasDraft
            ? 'edit-current-draft'
            : 'draft'
          : looksLikeQuestion
            ? 'qa'
            : hasDraft
              ? 'edit-current-draft'
              : 'draft';

      const responseHint: ScreenContextSlice['response_hint'] =
        inferredIntent === 'qa'
          ? { shape: 'chat-reply' }
          : hasDraft
            ? { shape: 'rewrite-paragraph' }
            : { shape: 'draft-paragraph' };

      const slice: ScreenContextSlice = {
        intent: inferredIntent,
        active_section_id: inferredIntent === 'qa' ? undefined : targetSectionId,
        current_draft: inferredIntent === 'qa' ? undefined : currentDraft,
        ui_state: { panel: 'chat-open', last_user_action: 'chat-send' },
        response_hint: responseHint,
        ...options?.sliceOverride,
      };
      await sendChat(trimmed, slice);
    },
    [currentSectionId, drafts, sendChat]
  );

  const value = useMemo<Prototype3ContextValue>(
    () => ({
      currentScreen,
      goToScreen,
      chatHistory,
      isChatPending,
      sendChat,
      sendUserMessage,
      drafts,
      sectionStatuses,
      currentSectionId,
      setCurrentSectionId,
      sectionDropdownOpen,
      setSectionDropdownOpen,
      toggleSectionDropdown,
    }),
    [
      currentScreen,
      goToScreen,
      chatHistory,
      isChatPending,
      sendChat,
      sendUserMessage,
      drafts,
      sectionStatuses,
      currentSectionId,
      sectionDropdownOpen,
      toggleSectionDropdown,
    ]
  );

  return <Prototype3Context.Provider value={value}>{children}</Prototype3Context.Provider>;
};

const usePrototype3 = () => {
  const ctx = useContext(Prototype3Context);
  if (!ctx) throw new Error('usePrototype3 must be used inside Prototype3Provider');
  return ctx;
};

export { Prototype3Provider, usePrototype3 };
export type { ScreenId, ChatMessage };
